// src/pages/Admin/pos/SugerenciasPanel.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchSugerencias, SugerenciaCita } from '../../../services/sugerenciaCitaService';
import {
    CalendarDays, Clock, Tag, Eye, EyeOff, Sparkles,
    Search, User, Home, ChevronRight, RefreshCw,
    Building2, MapPin, ArrowRight, CheckCircle2,
    TicketPercent, Layers
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';

const TIMEZONE = 'America/La_Paz';

// ── Paleta Pasantías Corporativa ─────────────────────────────
const C = {
    brand:        '#61257b',   // Púrpura primario
    brandDark:    '#3d1250',   // Púrpura oscuro
    brandLight:   '#8b3fa8',   // Púrpura suave
    accent1:      '#c1348b',   // Magenta / Acento principal
    accent2:      '#ea8406',   // Naranja cálido
    accent3:      '#ffd97c',   // Amarillo dorado
    success:      '#059669',   // Esmeralda
    slate:        '#0f172a',
    muted:        '#64748b',
    border:       '#f1f5f9',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
.sug-page * { font-family:'Plus Jakarta Sans',sans-serif; box-sizing:border-box; }

@keyframes sugFadeUp { from{opacity:0;transform:translateY(16px);} to{opacity:1;transform:translateY(0);} }
@keyframes spin { to{transform:rotate(360deg);} }

.sug-fadeup   { animation:sugFadeUp .45s cubic-bezier(.16,1,.3,1) both; }
.sug-fadeup-1 { animation:sugFadeUp .45s .06s cubic-bezier(.16,1,.3,1) both; }
.sug-fadeup-2 { animation:sugFadeUp .45s .12s cubic-bezier(.16,1,.3,1) both; }

.sug-card {
    background:#fff;
    border-radius:20px;
    border:1.5px solid #f1f5f9;
    box-shadow:0 4px 20px rgba(0,0,0,0.04);
    transition:all .28s cubic-bezier(.16,1,.3,1);
    display:flex;
    flex-direction:column;
    overflow:hidden;
}
.sug-card:hover {
    transform:translateY(-3px);
    box-shadow:0 16px 36px rgba(97,37,123,0.08);
    border-color:rgba(193,52,139,0.25);
}

.sug-input {
    width:100%;
    padding:10px 14px 10px 38px;
    border-radius:12px;
    border:1.5px solid #e2e8f0;
    background:#f8fafc;
    font-size:.84rem;
    color:#1e293b;
    outline:none;
    transition:all .2s;
}
.sug-input:focus {
    border-color:${C.brand};
    background:#fff;
    box-shadow:0 0 0 3px rgba(97,37,123,0.12);
}

.custom-scroll::-webkit-scrollbar { height:6px; width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color:rgba(97,37,123,.25); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color:rgba(97,37,123,.45); }
`;

export default function SugerenciasCardList() {
    const navigate = useNavigate();
    const [sugerencias, setSugerencias] = useState<SugerenciaCita[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroEstado, setFiltroEstado] = useState<'todas' | 'nuevas' | 'vistas' | 'con_descuento'>('todas');

    const loadData = () => {
        setLoading(true);
        fetchSugerencias()
            .then(data => {
                const sortedData = [...data].sort((a, b) => b.id - a.id);
                setSugerencias(sortedData);
                setLoading(false);
            })
            .catch(error => {
                /* Browser diagnostics omitted. */
                setLoading(false);
            });
    };

    useEffect(() => {
        loadData();
    }, []);

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '—';
        try {
            const zoned = toZonedTime(dateStr, TIMEZONE);
            return format(zoned, 'dd/MM/yyyy', { locale: es });
        } catch {
            return 'Fecha inválida';
        }
    };

    const formatTime = (timeStr?: string | null) => {
        if (!timeStr) return '';
        return timeStr.slice(0, 5);
    };

    const getInitials = (name?: string) => {
        if (!name) return 'S';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        return name.slice(0, 2).toUpperCase();
    };

    // Estadísticas
    const stats = useMemo(() => {
        const total = sugerencias.length;
        const nuevas = sugerencias.filter(s => !s.vista).length;
        const vistas = sugerencias.filter(s => !!s.vista).length;
        const conDescuento = sugerencias.filter(s => s.descuento_sugerido && Number(s.descuento_sugerido) > 0).length;
        return { total, nuevas, vistas, conDescuento };
    }, [sugerencias]);

    // Filtrado
    const sugerenciasFiltradas = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        return sugerencias.filter(s => {
            const matchesTerm = !term ||
                (s.cliente_nombre || '').toLowerCase().includes(term) ||
                (s.servicio_sugerido_nombre || '').toLowerCase().includes(term) ||
                (s.espacio_servicio_nombre || '').toLowerCase().includes(term) ||
                (s.plan_nombre || '').toLowerCase().includes(term) ||
                (s.motivo || '').toLowerCase().includes(term) ||
                (s.cupon_codigo || '').toLowerCase().includes(term);

            let matchesTab = true;
            if (filtroEstado === 'nuevas') matchesTab = !s.vista;
            else if (filtroEstado === 'vistas') matchesTab = !!s.vista;
            else if (filtroEstado === 'con_descuento') matchesTab = Boolean(s.descuento_sugerido && Number(s.descuento_sugerido) > 0);

            return matchesTerm && matchesTab;
        });
    }, [sugerencias, searchTerm, filtroEstado]);

    return (
        <>
            <style>{css}</style>
            <div className="sug-page min-h-screen bg-[#f8fafc] p-4 sm:p-6 lg:p-8 space-y-6">

                {/* ── HEADER SUPERIOR ── */}
                <div className="sug-fadeup bg-white p-5 sm:p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <nav className="flex items-center gap-2 text-xs font-semibold text-slate-400 mb-2">
                            <Link to="/pos-list" className="flex items-center gap-1.5 hover:text-[#61257b] transition-colors">
                                <Home size={13} /> Punto de Venta
                            </Link>
                            <ChevronRight size={12} />
                            <span className="text-[#61257b] font-bold">Citas Recomendadas</span>
                        </nav>

                        <div className="flex items-center gap-3.5">
                            <div
                                className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shadow-md flex-shrink-0"
                                style={{ background: `linear-gradient(135deg, ${C.brand}, ${C.accent1})` }}
                            >
                                <Sparkles size={22} />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                                    Citas <span style={{ color: C.brand }}>Recomendadas</span>
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                                        Motor Inteligente
                                    </span>
                                </h1>
                                <p className="text-xs sm:text-sm text-slate-500 font-medium">
                                    Sugerencias automatizadas basadas en patrones de reserva, consumo y frecuencia de los coworkers
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2.5 flex-wrap">
                        <button
                            onClick={loadData}
                            className="px-4 py-2 text-xs sm:text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all flex items-center gap-2"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualizar
                        </button>
                        <Link
                            to="/reservas"
                            className="px-4 py-2 text-xs sm:text-sm font-bold text-purple-800 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl transition-all flex items-center gap-2"
                        >
                            <Layers size={14} /> Ver Órdenes
                        </Link>
                        <Link
                            to="/point-of-sale"
                            className="px-4 py-2 text-xs sm:text-sm font-extrabold text-white rounded-xl shadow-md transition-all flex items-center gap-2 hover:opacity-95"
                            style={{ background: `linear-gradient(135deg, ${C.brand}, ${C.accent1})` }}
                        >
                            Ir al POS <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>

                {/* ── STATS BAR ── */}
                <div className="sug-fadeup-1 grid grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4">
                    <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-[#61257b] bg-[#61257b]/10 flex-shrink-0">
                            <Sparkles size={18} />
                        </div>
                        <div>
                            <span className="text-[0.68rem] font-bold text-slate-400 uppercase tracking-wider block">Total Generadas</span>
                            <span className="text-xl font-black text-slate-900">{stats.total}</span>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-[#ea8406] bg-[#ea8406]/10 flex-shrink-0">
                            <EyeOff size={18} />
                        </div>
                        <div>
                            <span className="text-[0.68rem] font-bold text-slate-400 uppercase tracking-wider block">Nuevas (Sin ver)</span>
                            <span className="text-xl font-black text-[#ea8406]">{stats.nuevas}</span>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-emerald-600 bg-emerald-50 flex-shrink-0">
                            <Eye size={18} />
                        </div>
                        <div>
                            <span className="text-[0.68rem] font-bold text-slate-400 uppercase tracking-wider block">Vistas por Cliente</span>
                            <span className="text-xl font-black text-emerald-600">{stats.vistas}</span>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-[#c1348b] bg-[#c1348b]/10 flex-shrink-0">
                            <TicketPercent size={18} />
                        </div>
                        <div>
                            <span className="text-[0.68rem] font-bold text-slate-400 uppercase tracking-wider block">Con Descuento</span>
                            <span className="text-xl font-black text-[#c1348b]">{stats.conDescuento}</span>
                        </div>
                    </div>
                </div>

                {/* ── BARRA DE BÚSQUEDA Y FILTROS ── */}
                <div className="sug-fadeup-1 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <div className="relative flex-1 max-w-md">
                        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Buscar por cliente, servicio, espacio o cupón..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="sug-input"
                        />
                    </div>

                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 custom-scroll">
                        {[
                            { id: 'todas', label: 'Todas', count: stats.total },
                            { id: 'nuevas', label: 'Nuevas', count: stats.nuevas },
                            { id: 'vistas', label: 'Vistas', count: stats.vistas },
                            { id: 'con_descuento', label: 'Con Oferta', count: stats.conDescuento },
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setFiltroEstado(t.id as any)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                                    filtroEstado === t.id
                                        ? 'bg-[#61257b] text-white shadow-sm'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                <span>{t.label}</span>
                                <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                                    filtroEstado === t.id ? 'bg-white/20 text-white' : 'bg-black/5 text-slate-500'
                                }`}>
                                    {t.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── LISTADO DE TARJETAS ── */}
                {loading ? (
                    <div className="bg-white rounded-3xl p-16 border border-slate-100 flex flex-col items-center justify-center gap-3">
                        <div
                            className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
                            style={{ borderColor: `${C.brand} transparent transparent transparent` }}
                        />
                        <p className="text-sm font-bold text-slate-500">Cargando sugerencias de citas...</p>
                    </div>
                ) : sugerenciasFiltradas.length === 0 ? (
                    <div className="bg-white rounded-3xl p-16 border border-slate-100 text-center space-y-3">
                        <div className="w-14 h-14 rounded-2xl bg-purple-50 text-[#61257b] flex items-center justify-center mx-auto">
                            <Sparkles size={28} />
                        </div>
                        <h3 className="text-base font-bold text-slate-800">No se encontraron sugerencias</h3>
                        <p className="text-xs text-slate-500 max-w-sm mx-auto">
                            {searchTerm || filtroEstado !== 'todas'
                                ? 'No hay sugerencias que coincidan con tus filtros actuales. Intenta limpiarlos.'
                                : 'Actualmente no hay sugerencias automáticas generadas para los clientes.'}
                        </p>
                        {(searchTerm || filtroEstado !== 'todas') && (
                            <button
                                onClick={() => { setSearchTerm(''); setFiltroEstado('todas'); }}
                                className="px-4 py-2 text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-xl transition-all"
                            >
                                Restablecer filtros
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="sug-fadeup-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        {sugerenciasFiltradas.map((s) => {
                            const dia = s.fecha_inicio ? format(toZonedTime(s.fecha_inicio, TIMEZONE), 'EEEE', { locale: es }) : '—';
                            const fechaInicio = formatDate(s.fecha_inicio);
                            const fechaFin = formatDate(s.fecha_fin);
                            const horaInicio = formatTime(s.hora_inicio);
                            const horaFin = formatTime(s.hora_fin);
                            const hasDiscount = s.descuento_sugerido && Number(s.descuento_sugerido) > 0;

                            return (
                                <div key={s.id} className="sug-card">
                                    {/* Barra superior de acento */}
                                    <div
                                        style={{
                                            height: '4px',
                                            background: hasDiscount
                                                ? `linear-gradient(90deg, ${C.accent1}, ${C.accent2})`
                                                : `linear-gradient(90deg, ${C.brand}, ${C.brandLight})`
                                        }}
                                    />

                                    <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                                        {/* Cabecera del Coworker */}
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div
                                                    className="w-10 h-10 rounded-xl text-white font-extrabold text-sm flex items-center justify-center flex-shrink-0 shadow-sm"
                                                    style={{ background: `linear-gradient(135deg, ${C.brand}, ${C.accent1})` }}
                                                >
                                                    {getInitials(s.cliente_nombre)}
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="font-extrabold text-slate-900 text-sm truncate">
                                                        {s.cliente_nombre || 'Cliente sin nombre'}
                                                    </h3>
                                                    <span className="text-[10px] font-mono text-slate-400 block">
                                                        Sugerencia #{s.id} • {formatDate(s.fecha_generada)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Estado Visto / No Visto */}
                                            {s.vista ? (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1 flex-shrink-0">
                                                    <CheckCircle2 size={11} /> Vista
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1 flex-shrink-0">
                                                    <EyeOff size={11} /> Pendiente
                                                </span>
                                            )}
                                        </div>

                                        {/* Servicio y Espacio */}
                                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-xs font-extrabold text-[#61257b] truncate">
                                                    {s.servicio_sugerido_nombre || 'Servicio sugerido'}
                                                </span>
                                                {s.categoria_nombre && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 bg-white text-slate-600 rounded-md border border-slate-200 flex-shrink-0">
                                                        {s.categoria_nombre}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                                {s.espacio_servicio_nombre && (
                                                    <span className="inline-flex items-center gap-1 font-medium">
                                                        <MapPin size={12} className="text-[#c1348b]" /> {s.espacio_servicio_nombre}
                                                    </span>
                                                )}
                                                {s.plan_nombre && (
                                                    <span className="inline-flex items-center gap-1 font-medium text-slate-500">
                                                        • Plan {s.plan_nombre}
                                                        {s.cantidad_periodos && ` (${s.cantidad_periodos})`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Fecha y Horario */}
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className="flex items-center gap-2 p-2 rounded-xl bg-purple-50/50 border border-purple-100/60 text-slate-700">
                                                <CalendarDays size={14} className="text-[#61257b] flex-shrink-0" />
                                                <div className="truncate">
                                                    <span className="text-[10px] text-slate-400 block uppercase font-bold">Fecha</span>
                                                    <span className="font-bold truncate capitalize">{dia.slice(0, 3)} {fechaInicio}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 p-2 rounded-xl bg-purple-50/50 border border-purple-100/60 text-slate-700">
                                                <Clock size={14} className="text-[#c1348b] flex-shrink-0" />
                                                <div className="truncate">
                                                    <span className="text-[10px] text-slate-400 block uppercase font-bold">Horario</span>
                                                    <span className="font-bold truncate">{horaInicio && horaFin ? `${horaInicio} - ${horaFin}` : 'Flexible'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Motivo sugerido */}
                                        {s.motivo && (
                                            <div className="p-2.5 rounded-xl bg-slate-50/80 border-l-4 border-[#c1348b] text-[11px] text-slate-600 italic">
                                                "{s.motivo}"
                                            </div>
                                        )}

                                        {/* Cupón si aplica */}
                                        {s.cupon_codigo && (
                                            <div className="flex items-center justify-between p-2 rounded-xl bg-amber-50/70 border border-dashed border-amber-300 text-amber-900 text-xs font-semibold">
                                                <span className="flex items-center gap-1.5">
                                                    <Tag size={13} className="text-[#ea8406]" /> Cupón aplicable:
                                                </span>
                                                <span className="font-mono font-bold bg-white px-2 py-0.5 rounded border border-amber-200">
                                                    {s.cupon_codigo}
                                                </span>
                                            </div>
                                        )}

                                        {/* Desglose Financiero y CTA */}
                                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
                                            <div>
                                                {hasDiscount && s.subtotal && (
                                                    <span className="text-[11px] line-through text-slate-400 mr-2">
                                                        Bs {s.subtotal}
                                                    </span>
                                                )}
                                                <span className="text-base font-black text-slate-900">
                                                    Bs {s.total ?? s.subtotal ?? 0}
                                                </span>
                                                {hasDiscount && (
                                                    <span className="text-[10px] font-bold text-emerald-600 ml-1.5 block">
                                                        Ahorro: -Bs {s.descuento_sugerido}
                                                    </span>
                                                )}
                                            </div>

                                            <button
                                                onClick={() => navigate('/point-of-sale')}
                                                className="px-3.5 py-2 rounded-xl text-xs font-bold text-white flex items-center gap-1.5 shadow-sm hover:opacity-95 transition-opacity"
                                                style={{ background: `linear-gradient(135deg, ${C.brand}, ${C.accent1})` }}
                                            >
                                                Agendar <ArrowRight size={13} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
